import { AsyncPipe } from "@angular/common";
import { Component, DestroyRef } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { FormlyFieldConfig, FormlyForm } from "@ngx-formly/core";
import * as Sentry from "@sentry/angular";
import { posthog } from "posthog-js";
import { ConfirmationService } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { ConfirmDialog } from "primeng/confirmdialog";
import { DialogModule } from "primeng/dialog";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { BehaviorSubject, combineLatest, finalize, map, Observable } from "rxjs";
import { ApprovalGroupDialogConfig } from "./approval-group-dialog.types";
import { getFormFields } from "./approval-group-form-definition";
import { ApprovalGroupOverview } from "../../../../domain/approval-group";
import { ApprovalGroupService } from "../../../../services/approval-group.service";
import { FormService } from "../../../../services/form.service";
import { ToastService } from "../../../../services/toast.service";
import { primeNgPageModules } from "../../../../shared/modules/prime-ng-common.module";
import { FormlyFormOptionsWithFormState } from "../../../../shared/types/form";

interface ApprovalGroupFormModel {
  name: string;
  userIds: number[];
}

@Component({
  templateUrl: "./approval-group.dialog.html",
  imports: [
    ...primeNgPageModules,
    ReactiveFormsModule,
    FormlyForm,
    DialogModule,
    ButtonModule,
    TranslocoDirective,
    ConfirmDialog,
    AsyncPipe,
  ],
  providers: [ConfirmationService],
})
export class ApprovalGroupDialog {
  public form: FormGroup = new FormGroup({});
  public model: ApprovalGroupFormModel = {} as ApprovalGroupFormModel;
  public fields: FormlyFieldConfig[];
  private orgId: number;
  public approvalGroup?: ApprovalGroupOverview;
  public options: FormlyFormOptionsWithFormState = {
    formState: {
      disabled: false,
    },
  };
  public submitLoading = new BehaviorSubject<boolean>(false);
  public deleteLoading = new BehaviorSubject<boolean>(false);
  public loading: Observable<boolean> = combineLatest([this.submitLoading, this.deleteLoading]).pipe(
    map(([submitState, deleteState]) => {
      return submitState || deleteState;
    }),
  );

  constructor(
    public readonly ref: DynamicDialogRef,
    private readonly formService: FormService,
    private readonly config: DynamicDialogConfig<ApprovalGroupDialogConfig>,
    private readonly approvalGroupService: ApprovalGroupService,
    private readonly translocoService: TranslocoService,
    private readonly toastService: ToastService,
    private readonly confirmationService: ConfirmationService,
    private readonly destroyRef: DestroyRef,
  ) {
    if (!this.config.data?.orgId) {
      const error = new Error(`Organization ID not found`);
      Sentry.captureException(error);
      this.toastService.error(
        this.translocoService.translate("app.common.errors.loadOrganization", { error: error.message }),
      );
      this.ref.close();
      throw error;
    }
    this.orgId = this.config.data.orgId;
    this.approvalGroup = this.config.data?.approvalGroup;
    this.model = {
      name: this.approvalGroup?.name || "",
      userIds: this.approvalGroup?.users.map((u) => u.id) || [],
    };

    this.fields = this.formService.prepareFormFields(getFormFields());
  }

  public submitForm(): void {
    this.submitLoading.next(true);
    this.options.formState.disabled = true;

    if (this.approvalGroup?.id) {
      this.approvalGroupService
        .updateApprovalGroup$(this.orgId, this.approvalGroup.id, this.form.value)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => {
            this.submitLoading.next(false);
            this.options.formState.disabled = false;
          }),
        )
        .subscribe({
          next: (response) => {
            this.toastService.success(
              this.translocoService.translate("app.organization-settings.approvalGroups.editSuccess"),
            );

            posthog.capture("approval_group_updated");

            this.ref.close({ approvalGroup: response });
          },
          error: () => {
            this.toastService.error(this.translocoService.translate("app.common.errors.saveError"));
          },
        });
    } else {
      this.approvalGroupService
        .createApprovalGroup$(this.orgId, this.form.value)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => {
            this.submitLoading.next(false);
            this.options.formState.disabled = false;
          }),
        )
        .subscribe({
          next: (response) => {
            this.toastService.success(
              this.translocoService.translate("app.organization-settings.approvalGroups.createSuccess"),
            );

            posthog.capture("approval_group_created");

            this.ref.close({ approvalGroup: response });
          },
          error: () => {
            this.toastService.error(this.translocoService.translate("app.common.errors.saveError"));
          },
        });
    }
  }

  public openConfirmationDeleteDialog(): void {
    this.confirmationService.confirm({
      header: this.translocoService.translate("app.organization-settings.approvalGroups.deleteGroup"),
      message:
        this.translocoService.translate("app.organization-settings.approvalGroups.deleteGroupConfirmation") +
        "<br/><br/>" +
        this.translocoService.translate("app.organization-settings.approvalGroups.deleteGroupWarning"),
      icon: "pi pi-exclamation-triangle",
      rejectButtonProps: {
        label: this.translocoService.translate("app.common.cancel"),
        severity: "secondary",
        outlined: true,
      },
      acceptButtonProps: {
        label: this.translocoService.translate("app.common.delete"),
        severity: "danger",
      },
      accept: () => {
        const approvalGroup = this.approvalGroup;
        if (!approvalGroup) {
          this.toastService.error(this.translocoService.translate("app.common.errors.deleteError"));
          return;
        }

        this.deleteLoading.next(true);
        this.options.formState.disabled = true;

        this.approvalGroupService
          .deleteApprovalGroup$(this.orgId, approvalGroup.id)
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            finalize(() => {
              this.deleteLoading.next(false);
              this.options.formState.disabled = false;
            }),
          )
          .subscribe({
            next: () => {
              this.toastService.success(
                this.translocoService.translate("app.organization-settings.approvalGroups.deleteGroupSuccess"),
              );

              posthog.capture("approval_group_deleted");

              this.ref.close({ approvalGroup, isDeleted: true });
            },
            error: () => {
              this.toastService.error(this.translocoService.translate("app.common.errors.deleteError"));
            },
          });
      },
    });
  }
}
